/**
 * useChoreTimer — live countdown timer driven by server PendingChores state.
 * No client-side scheduling logic — just counts down to due_at or expires_at.
 */
import { useState, useEffect } from "react";
import { formatTimeRemaining, type ChoreStatus } from "@/lib/chore-scheduler";

export interface PendingInfo {
  status: ChoreStatus;
  due_at: string;
  expires_at: string;
  completed_at?: string | null;
}

export interface ChoreTimerState {
  status: ChoreStatus;
  label: string;
}

/**
 * Derive the visual timer state from server-provided pending info.
 * Ticks every second when due (counting down grace), every minute when pending.
 */
export function usePendingTimer(pending: PendingInfo | null): ChoreTimerState {
  const [now, setNow] = useState(Date.now());

  const status = pending?.status ?? "pending";
  const tickRate = status === "due" ? 1000 : 60_000;

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), tickRate);
    return () => clearInterval(id);
  }, [tickRate]);

  if (!pending) {
    return { status: "pending", label: "⏳ Waiting..." };
  }

  switch (pending.status) {
    case "pending": {
      const ms = new Date(pending.due_at).getTime() - now;
      return {
        status: "pending",
        label: ms > 0 ? `🔒 ${formatTimeRemaining(ms)}` : "🔒 Soon...",
      };
    }
    case "due": {
      const ms = new Date(pending.expires_at).getTime() - now;
      return {
        status: "due",
        label: ms > 0 ? `⏳ ${formatTimeRemaining(ms)} left` : "⏳ Expiring...",
      };
    }
    case "completed": {
      return { status: "completed", label: "Done ✓" };
    }
    case "failed": {
      return { status: "failed", label: "Overdue!" };
    }
  }
}

/**
 * Get CSS classes for a chore status.
 */
export function getStatusColor(status: ChoreStatus): string {
  switch (status) {
    case "completed": return "text-app-text-muted";
    case "pending": return "text-app-info";
    case "due": return "text-app-success";
    case "failed": return "text-app-danger";
  }
}

export function getStatusBg(status: ChoreStatus): string {
  switch (status) {
    case "completed": return "bg-app-text-muted/10";
    case "pending": return "bg-app-info/10";
    case "due": return "bg-app-success/10";
    case "failed": return "bg-app-danger/20";
  }
}
